import { alpha } from "@mui/material/styles";
import { ICustomTheme } from "../types/ICustomTheme";
import {
  MODERN_BORDER,
  MODERN_KEYFRAMES,
  MODERN_LOADING_STATES,
} from "./modernVariables";

export const getCssBaselineStyles = (themeOptions: ICustomTheme) => ({
  styleOverrides: {
    // Keyframes modernos
    "@keyframes shimmer": MODERN_KEYFRAMES.shimmer,
    "@keyframes pulse": MODERN_KEYFRAMES.pulse,
    "@keyframes slideIn": MODERN_KEYFRAMES.slideUp,
    "@keyframes fadeIn": MODERN_KEYFRAMES.fadeIn,
    "@keyframes float": MODERN_KEYFRAMES.float,
    "@keyframes breathe": MODERN_KEYFRAMES.breathe,
    "@keyframes ripple": MODERN_KEYFRAMES.ripple,
    "@keyframes scaleIn": MODERN_KEYFRAMES.scaleIn,

    // Keyframes adicionais para transições de componentes
    "@keyframes componentEnter": {
      "0%": {
        opacity: 0,
        transform: "translateY(20px) scale(0.95)",
      },
      "100%": {
        opacity: 1,
        transform: "translateY(0) scale(1)",
      },
    },
    "@keyframes componentExit": {
      "0%": {
        opacity: 1,
        transform: "translateY(0) scale(1)",
      },
      "100%": {
        opacity: 0,
        transform: "translateY(-20px) scale(0.95)",
      },
    },
    "@keyframes slideInLeft": {
      "0%": {
        opacity: 0,
        transform: "translateX(-30px)",
      },
      "100%": {
        opacity: 1,
        transform: "translateX(0)",
      },
    },
    "@keyframes slideInRight": {
      "0%": {
        opacity: 0,
        transform: "translateX(30px)",
      },
      "100%": {
        opacity: 1,
        transform: "translateX(0)",
      },
    },

    // Classes utilitárias modernas
    ".loading-shimmer": MODERN_LOADING_STATES.skeleton(themeOptions),
    ".loading-pulse": MODERN_LOADING_STATES.pulse,
    ".loading-breathe": MODERN_LOADING_STATES.breathe,
    ".fade-in-element": {
      animation: "fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    ".float-element": {
      animation: "float 3s ease-in-out infinite",
    },

    // Classes para transições de componentes
    ".component-entering": {
      animation: "componentEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    ".component-entered": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
    ".component-exiting": {
      animation: "componentExit 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    ".component-exited": {
      opacity: 0,
      transform: "translateY(-20px) scale(0.95)",
    },
    ".slide-in-left": {
      animation: "slideInLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    ".slide-in-right": {
      animation: "slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    ".scale-in": {
      animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },

    // Scrollbar moderna e elegante
    "*::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "*::-webkit-scrollbar-track": {
      marginBlock: "4px",
      background:
        themeOptions.palette.mode === "dark"
          ? alpha(themeOptions.palette.background.paper, 0.3)
          : alpha(themeOptions.palette.background.paper, 0.6),
    },
    "*::-webkit-scrollbar-thumb": {
      background:
        themeOptions.palette.mode === "dark"
          ? alpha(themeOptions.palette.text.secondary, 0.4)
          : alpha(themeOptions.palette.text.secondary, 0.3),

      transition: "background 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

      "&:hover": {
        background:
          themeOptions.palette.mode === "dark"
            ? alpha(themeOptions.palette.text.secondary, 0.6)
            : alpha(themeOptions.palette.text.secondary, 0.5),
      },
    },

    // Melhoria dos seletores de data/hora com melhor contraste
    'input[type="time"]::-webkit-calendar-picker-indicator, input[type="date"]::-webkit-calendar-picker-indicator, input[type="datetime-local"]::-webkit-calendar-picker-indicator':
      {
        filter:
          themeOptions.palette.mode === "dark"
            ? "invert(1) brightness(0.8)"
            : "invert(0) brightness(1.2)",
        transition: "filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

        "&:hover": {
          filter:
            themeOptions.palette.mode === "dark"
              ? "invert(1) brightness(1)"
              : "invert(0) brightness(1.4)",
        },
      },

    // Seleção de texto moderna
    "::selection": {
      backgroundColor: alpha(themeOptions.palette.primary.main, 0.3),
      color: themeOptions.palette.text.primary,
    },

    // Focus outline moderno para acessibilidade
    "*:focus-visible": {
      outline: `2px solid ${alpha(themeOptions.palette.primary.main, 0.6)}`,
      outlineOffset: "2px",
      borderRadius: MODERN_BORDER.radius.sm,
    },

    // Smooth scrolling global
    html: {
      scrollBehavior: "smooth",
    },

    // Body com transições suaves
    body: {
      transition:
        "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },

    // Melhorias em links
    a: {
      transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        color: themeOptions.palette.primary.main,
      },
    },
    // Estilo moderno para elementos desabilitados
    "*:disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
      filter: "grayscale(0.3)",
      // Removendo transition para evitar conflitos com mudanças de estado dos componentes
    },
  },
});
